import { useEffect, useState } from 'react';
import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import styled from 'styled-components';

import { useNavigate } from 'react-router-dom';
import SignLayout from 'layout/JoinLayout';
import DefaultInput from 'components/input/DefaultInput';
import DefaultButton from 'components/button/DefaultButton';

import { useMutation } from '@tanstack/react-query';
import { restfetcher } from 'queryClient';
import modalInformationAtom from 'atom/modalInformationAtom';
import { useAtom, useSetAtom } from 'jotai';
import userInforAtom from 'atom/userInfoAtom';
import { setError } from 'utils/common';
import companyInforAtom from 'atom/companyInfoAtom';
import { useResetAtom } from 'jotai/utils';

const Login = () => {
  const navigate = useNavigate();

  const setModalInformation = useSetAtom(modalInformationAtom);

  const [isLogin, setIsLogin] = useState({
    id: '',
    password: ''
  });

  const [userInfor, setUserInfor] = useAtom(userInforAtom);
  const [companyInfor, setCompanyInfor] = useAtom(companyInforAtom);
  const resetUserInfoAtom = useResetAtom(userInforAtom);
  const resetCompanyInfoAtom = useResetAtom(companyInforAtom);

  const loginApi = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'v1/auth/login',
        params: {
          usremail: id,
          usrpw: password,
          serviceid: '92b97290cf7448119109876a25aa2509'
        }
      }),

    onSuccess: (data: any) => {
      if (data.result === 'success') {
        const { authenticatetoken, userinfo, companyInfo } = data;
        setUserInfor(userinfo);
        if (companyInfo) {
          setCompanyInfor(companyInfo);
          navigate('/');
        } else {
          setModalInformation({
            type: 'BIZMEMBER_JOIN',
            handleClickLeftBtn: () => {
              // 통합 로그인 완료 후 비즈 가입 여부를 알 수 있기 때문에
              // 비즈 멤버십 가입 유도 모달에서 "아니오" 버튼을 누르면
              // 로그아웃 처리

              logoutApi.mutate();
            },
            handleClickRightBtn: () => {
              navigate('/join', {
                state: {
                  loginId: isLogin.id
                }
              });
            }
          });
        }

        sessionStorage?.setItem('token', authenticatetoken?.jwt);
        localStorage?.setItem('refresh', authenticatetoken?.refresh);
      } else if (data.result === 'failed') {
        if (data?.errcode) {
          if (data.errcode) {
            setModalInformation({
              type: 'API_ERROR',
              message: setError(data.errcode)
            });
          }
        }
      }
    },
    onError: (error: any) => {
      console.log(error);
    }
  });

  const logoutApi = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'v1/auth/logout',
        params: {}
      }),

    onSuccess: (data: any) => {
      if (data.result === 'success') {
        window.localStorage.clear();
        window.sessionStorage.clear();
        resetUserInfoAtom();
        resetCompanyInfoAtom();
      }
    },
    onError: (error: any) => {
      console.log(error);
    }
  });

  const handleLogin = async () => {
    loginApi.mutate();
  };

  const handleClickGoToJoin = () => {
    navigate('/join');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLogin();
    }
  };

  const { id, password } = isLogin;

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { value, name } = e.currentTarget;
    setIsLogin({
      ...isLogin,
      [name]: value
    });
  };

  useEffect(() => {
    if (userInfor.usridx !== 0 && companyInfor.companyidx !== 0) {
      navigate('/');
      window.localStorage.clear();
      window.sessionStorage.clear();
    }
  }, [userInfor.usridx]);

  return (
    <>
      <SignLayout text="엑스바디 서비스 이용을 위해 로그인 해주세요.">
        <section>
          <Desc>엑스바디 통합회원 정보와 동일한 정보를 입력해주세요.</Desc>
          <form onKeyDown={handleKeyDown} onSubmit={handleLogin}>
            <InputWrapper>
              <div>
                <h6>아이디</h6>
                <DefaultInput
                  name="id"
                  value={id}
                  onChange={handleChange}
                  width={320}
                  height="large"
                  placeholder="이메일을 입력해주세요"
                />
              </div>
              <div>
                <h6>비밀번호</h6>
                <DefaultInput
                  name="password"
                  value={password}
                  onChange={handleChange}
                  placeholder="패스워드를 입력해주세요"
                  width={320}
                  height="large"
                  password
                />
              </div>
              <ButtonWrapper>
                <DefaultButton
                  text="로그인"
                  type="primary"
                  style={{ width: '155px' }}
                  disabled={isLogin.id === '' || isLogin.password === ''}
                  onClick={handleLogin}
                />
                <DefaultButton
                  text="회원가입"
                  type="primary"
                  style={{ width: '155px' }}
                  onClick={handleClickGoToJoin}
                />
              </ButtonWrapper>
            </InputWrapper>
          </form>
        </section>
      </SignLayout>
    </>
  );
};

export default Login;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 320px;
  h6 {
    color: ${color.black2};
    font-weight: 500;
    line-height: 20px;
  }

  & div {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ant-btn {
    height: 48px;
  }
`;

const Desc = styled.p`
  ${font.h3_Regular};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row !important;
  gap: 10px !important;
`;
